import React, { useMemo } from "react";
import { Icons } from "../Icons";

/**
 * Badge type → CSS class mapping.
 * Each type applies a distinct background/text color pair from the semantic tokens.
 */
const BADGE_TYPES = {
    Success: "success",
    Pending: "pending",
    Warning: "warning",
    Error: "error",
    Neutral: "neutral",
    News: "news",
    Job: "job",
    Sec: "sec",
};

/**
 * Badge — Small status indicator with optional leading/trailing icons.
 *
 * Use for inline status labels like "Active", "Pending", "Error" etc.
 * Falls back to Neutral styling if an unrecognized type is passed.
 *
 * @param {string}  label           - Badge text (or use children)
 * @param {string}  type            - 'Success' | 'Pending' | 'Warning' | 'Error' | 'Neutral'
 * @param {boolean} ShowLeadingIcon - Show icon on the left
 * @param {boolean} ShowTrailingIcon- Show icon on the right
 * @param {string}  LeadingIcon     - Icon name for left position
 * @param {string}  TrailingIcon    - Icon name for right position
 */
export const Badge = ({
    children,
    label = "",
    type = "Neutral",
    ShowLeadingIcon = false,
    ShowTrailingIcon = false,
    LeadingIcon = null,
    TrailingIcon = null,
    LeadingIconProps = {},
    TrailingIconProps = {},
    fontClass = "body-s-semibold",
    customClassName = "",
    customStyle = {},
}) => {
    const typeClass = BADGE_TYPES[type] || BADGE_TYPES.Neutral;

    const computedClass = useMemo(() =>
        [
            "badge-container",
            'badge-' + typeClass,
            fontClass,
            customClassName,
        ].filter(Boolean).join(" "),
        [typeClass, fontClass, customClassName]
    );

    return (
        <div className={computedClass} style={customStyle}>
            {ShowLeadingIcon && LeadingIcon && (
                <Icons 
                    name={LeadingIcon} 
                    size={16}
                    {...LeadingIconProps}
                />
            )}
            {/* Display label prop or fall back to children */}
            <span className="badge-text">{label || children}</span>
            {ShowTrailingIcon && TrailingIcon && (
                <Icons 
                    name={TrailingIcon} 
                    size={16}
                    {...TrailingIconProps} 
                    {...TrailingIconProps}
                />
            )}
        </div>
    );
};
